import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuSearch, LuX, LuCheck, LuLandmark } from 'react-icons/lu';
import { searchPseBanks, type PseBank } from '@/utils/pseBanks';

/**
 * Bottom sheet de selección de banco PSE — SELECCIÓN ÚNICA.
 *
 * Reproduce el lenguaje visual del bottom sheet de Tikipal Business (overlay oscuro +
 * blur, esquinas superiores muy redondeadas, drag handle, buscador con lupa, filas/tarjetas
 * blancas redondeadas, seleccionado en naranja). Al tocar un banco: se devuelve al padre
 * (que guarda el código real + regenera el formToken) y se cierra. Se puede cerrar con la X
 * conservando la selección previa. Móvil = hoja inferior; desktop = modal centrado.
 *
 * Catálogo y códigos: `utils/pseBanks` (catálogo real PSE; búsqueda sin mayúsculas ni tildes).
 */
type Props = {
  open: boolean;
  onClose: () => void;
  selectedCode?: string | null;
  onSelect: (bank: PseBank) => void;
  isSpanish?: boolean;
};

const PseBankSheet = ({ open, onClose, selectedCode, onSelect, isSpanish = true }: Props) => {
  const [render, setRender] = useState(open);
  const [shown, setShown] = useState(false);
  const [query, setQuery] = useState('');
  // Teclado iOS: alto del teclado (kbInset) + alto visible (availH) vía visualViewport.
  const [kbInset, setKbInset] = useState(0);
  const [availH, setAvailH] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  // Montar + animar la entrada (slide-up + fade); al cerrar, animar salida y desmontar.
  useEffect(() => {
    if (open) {
      setRender(true);
      setQuery(''); // abre siempre con el listado completo
      const id = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(id);
    }
    setShown(false);
    setKbInset(0); // resetea el ajuste de teclado al cerrar
    const t = setTimeout(() => setRender(false), 240);
    return () => clearTimeout(t);
  }, [open]);

  // Cerrar con Escape (desktop).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // iOS Safari NO encoge el layout viewport (ni dvh) cuando aparece el teclado: la hoja,
  // anclada a bottom:0, queda DETRÁS del teclado y la lista se oculta. Con visualViewport
  // medimos el alto del teclado y subimos + encogemos la hoja para que quede por encima.
  useEffect(() => {
    if (!open || typeof window === 'undefined' || !window.visualViewport) return;
    const vv = window.visualViewport;
    const onResize = () => {
      const kb = Math.max(0, window.innerHeight - vv.offsetTop - vv.height);
      setKbInset(kb);
      setAvailH(vv.height);
    };
    onResize();
    vv.addEventListener('resize', onResize);
    vv.addEventListener('scroll', onResize);
    return () => {
      vv.removeEventListener('resize', onResize);
      vv.removeEventListener('scroll', onResize);
    };
  }, [open]);

  if (!render || typeof document === 'undefined') return null;

  const banks = searchPseBanks(query);

  const sheet = (
    // z por encima del overlay de pago (step-2, z-120). NO se auto-enfoca el buscador → sin
    // teclado al abrir. Móvil: hoja inferior; desktop (lg): modal centrado.
    <div className="fixed inset-0 z-[200] flex items-end justify-center lg:items-center" role="dialog" aria-modal="true">
      {/* Overlay oscurecido + desenfocado */}
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-200 ${shown ? 'opacity-100' : 'opacity-0'}`}
      />
      {/* Hoja. Con teclado abierto (kbInset>0): se sube `bottom:kbInset` para quedar sobre el
          teclado y se limita la altura al área visible (availH) para no meterse bajo la barra. */}
      <div
        style={
          kbInset > 0
            ? { bottom: kbInset, maxHeight: Math.max(240, availH - 12) }
            : undefined
        }
        className={`relative z-10 w-full lg:max-w-[440px] lg:mx-4 flex flex-col overflow-hidden bg-slate-50
          max-h-[80dvh] lg:max-h-[80vh]
          rounded-t-[28px] lg:rounded-[24px]
          shadow-[0_-24px_60px_-24px_rgba(15,23,42,0.4)] lg:shadow-[0_40px_100px_-30px_rgba(15,23,42,0.55)]
          pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:pb-4
          transition-transform duration-240 ease-out
          ${shown ? 'translate-y-0' : 'translate-y-full lg:translate-y-4 lg:opacity-0'}`}
      >
        {/* Drag handle (solo móvil) */}
        <div className="lg:hidden pt-3 pb-1 flex justify-center shrink-0">
          <span className="h-1.5 w-10 rounded-full bg-slate-300" />
        </div>

        {/* Título + cerrar */}
        <div className="flex items-center justify-between px-5 pt-2 lg:pt-5 pb-3 shrink-0">
          <h3 className="text-[18px] font-semibold tracking-tight text-slate-900">
            {isSpanish ? 'Selecciona tu banco' : 'Select your bank'}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={isSpanish ? 'Cerrar' : 'Close'}
            className="w-8 h-8 shrink-0 rounded-full bg-white ring-1 ring-slate-200/80 shadow-sm flex items-center justify-center text-slate-500 hover:text-slate-700 active:scale-95 transition-all"
          >
            <LuX size={16} />
          </button>
        </div>

        {/* Buscador (font-size 16px → sin autozoom iOS). Sin autoFocus → no abre el teclado. */}
        <div className="px-5 pb-3 shrink-0">
          <div className="flex items-center gap-2 h-[46px] rounded-[14px] border border-gray-200 bg-white px-3.5 focus-within:border-tikipal-orange/60 focus-within:ring-2 focus-within:ring-tikipal-orange/10 transition-all">
            <LuSearch size={16} className="text-gray-400 shrink-0" />
            <input
              type="text"
              inputMode="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={isSpanish ? 'Buscar banco' : 'Search bank'}
              className="flex-1 min-w-0 bg-transparent outline-none text-[16px] text-slate-800 placeholder:text-gray-400"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery('')}
                aria-label={isSpanish ? 'Limpiar' : 'Clear'}
                className="text-gray-400 hover:text-gray-600 shrink-0"
              >
                <LuX size={15} />
              </button>
            )}
          </div>
        </div>

        {/* Listado — scroll interno; el teclado no lo tapa (min-h-0 + flex-1). */}
        <div
          ref={listRef}
          className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 pt-1 pb-4 space-y-2.5"
        >
          {banks.length === 0 ? (
            <div className="py-10 text-center">
              <p className="text-[14px] text-slate-400">
                {isSpanish ? 'No se encontró el banco.' : 'No bank found.'}
              </p>
            </div>
          ) : (
            banks.map((b) => {
              const selected = b.code === selectedCode;
              return (
                <button
                  type="button"
                  key={b.code}
                  onClick={() => onSelect(b)}
                  className={`w-full flex items-center justify-between gap-3 rounded-[16px] border px-4 py-3.5 text-left transition-colors ${
                    selected
                      ? 'border-tikipal-orange bg-[#fff4ef]'
                      : 'border-gray-200/80 bg-white hover:border-gray-300 active:bg-gray-50'
                  }`}
                >
                  <span className="flex items-center gap-3 min-w-0">
                    <LuLandmark
                      size={18}
                      className={`shrink-0 ${selected ? 'text-tikipal-orange' : 'text-gray-400'}`}
                    />
                    <span className="text-[15px] text-slate-800 truncate">{b.name}</span>
                  </span>
                  {/* Indicador circular de selección (naranja si elegido). */}
                  <span
                    className={`shrink-0 w-[22px] h-[22px] rounded-full border-2 flex items-center justify-center transition-colors ${
                      selected ? 'border-tikipal-orange bg-tikipal-orange' : 'border-gray-300 bg-white'
                    }`}
                  >
                    {selected && <LuCheck size={13} strokeWidth={3} className="text-white" />}
                  </span>
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(sheet, document.body);
};

export default PseBankSheet;
